import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { CatButton } from "blocks-nextjs/blocks-react/components/Button/Button";
import { CatDatepickerField } from "blocks-nextjs/blocks-react/components/DatepickerField/DatepickerField";
import React from "react";
const MS_PER_DAY = 1000 * 60 * 60 * 24;
const MS_PER_MINUTE = 60000;
const MINUTES_PER_HOUR = 60;
const DATE_PAD_LENGTH = 2;
const DATE_PAD_CHAR = "0";
// Helper function to format date from ISO to MM/dd/yyyy
// Moved outside component to prevent recreation on every render
const formatDateToMMDDYYYY = (value) => {
    if (!value)
        return undefined;
    // If already in MM/dd/yyyy format, return as is
    const mmddyyyyPattern = /^\d{2}\/\d{2}\/\d{4}$/;
    if (mmddyyyyPattern.test(value)) {
        return value;
    }
    try {
        const date = new Date(value);
        // Check if date is valid
        if (isNaN(date.getTime()))
            return undefined;
        const month = String(date.getMonth() + 1).padStart(DATE_PAD_LENGTH, DATE_PAD_CHAR);
        const day = String(date.getDate()).padStart(DATE_PAD_LENGTH, DATE_PAD_CHAR);
        const year = date.getFullYear();
        return `${month}/${day}/${year}`;
    }
    catch (error) {
        console.error("Error formatting date:", error);
        return undefined;
    }
};
/**
 * Convert date from MM/dd/yyyy format to yyyy/MM/dd format
 * CatDatepickerField expects yyyy/MM/dd format for disabledMinDate and disabledMaxDate
 * @param dateString - Date string in MM/dd/yyyy format
 * @returns Date string in yyyy/MM/dd format, or undefined if conversion fails
 */
const convertToYYYYMMDD = (dateString) => {
    if (!dateString)
        return undefined;
    // If already in yyyy/MM/dd format, return as is
    const yyyymmddPattern = /^\d{4}\/\d{2}\/\d{2}$/;
    if (yyyymmddPattern.test(dateString)) {
        return dateString;
    }
    try {
        const [month, day, year] = dateString.split("/");
        // Validate parsed values
        if (!month || !day || !year) {
            return undefined;
        }
        return `${year}/${month}/${day}`;
    }
    catch (error) {
        console.error("Error converting date format:", error);
        return undefined;
    }
};
/**
 * Parse a date string in MM/dd/yyyy format to a Date object
 * @param dateString - Date string in MM/dd/yyyy format
 * @returns Date object normalized to midnight (00:00:00.000) or null if parsing fails
 */
const parseDateString = (dateString) => {
    if (!dateString)
        return null;
    try {
        const [month, day, year] = dateString.split("/").map(Number);
        // Validate parsed values
        if (isNaN(month) || isNaN(day) || isNaN(year)) {
            return null;
        }
        const date = new Date(year, month - 1, day);
        // Verify the date is valid (checks for invalid dates like 02/31/2026)
        if (isNaN(date.getTime())) {
            return null;
        }
        // Normalize to midnight to ensure consistent date comparisons
        // This matches the normalization done for the 'today' Date object
        date.setHours(0, 0, 0, 0);
        return date;
    }
    catch (error) {
        console.error("Error parsing date string:", error);
        return null;
    }
};
/**
 * Compare two date strings in MM/dd/yyyy format
 * @param date1 - First date string
 * @param date2 - Second date string
 * @returns -1 if date1 < date2, 0 if equal, 1 if date1 > date2, null if parsing fails
 */
const compareDates = (date1, date2) => {
    const d1 = parseDateString(date1);
    const d2 = parseDateString(date2);
    if (!d1 || !d2)
        return null;
    if (d1 < d2)
        return -1;
    if (d1 > d2)
        return 1;
    return 0;
};
/**
 * Validates a date range (start date and end date)
 * @param startDate - Start date string in MM/dd/yyyy format
 * @param endDate - End date string in MM/dd/yyyy format
 * @param maxDate - Maximum allowed date (typically today) in project timezone
 * @param maxRangeDays - Optional maximum number of days between start and end (inclusive)
 * @returns Validation result with isValid flag and optional reason
 */
const validateDateRange = (startDate, endDate, maxDate, maxRangeDays) => {
    // Check if both dates are provided
    if (!startDate || !endDate) {
        return { isValid: false, reason: "Both start and end dates are required" };
    }
    // Parse dates
    const startDateObj = parseDateString(startDate);
    const endDateObj = parseDateString(endDate);
    // Validate parsing succeeded
    if (!startDateObj || !endDateObj) {
        return { isValid: false, reason: "Failed to parse date strings" };
    }
    // For timezone-safe comparison, compare year/month/day components directly
    // rather than timestamps, since parseDateString creates dates in local TZ
    // but maxDate is in project TZ
    const maxYear = maxDate.getFullYear();
    const maxMonth = maxDate.getMonth();
    const maxDay = maxDate.getDate();
    // Validate start date is not in the future (compare date components)
    const startYear = startDateObj.getFullYear();
    const startMonth = startDateObj.getMonth();
    const startDay = startDateObj.getDate();
    if (startYear > maxYear ||
        (startYear === maxYear && startMonth > maxMonth) ||
        (startYear === maxYear && startMonth === maxMonth && startDay > maxDay)) {
        return { isValid: false, reason: "Start date cannot be a future date" };
    }
    // Validate end date is not in the future (compare date components)
    const endYear = endDateObj.getFullYear();
    const endMonth = endDateObj.getMonth();
    const endDay = endDateObj.getDate();
    if (endYear > maxYear ||
        (endYear === maxYear && endMonth > maxMonth) ||
        (endYear === maxYear && endMonth === maxMonth && endDay > maxDay)) {
        return { isValid: false, reason: "End date cannot be a future date" };
    }
    // Validate end date is not before start date
    if (endDateObj < startDateObj) {
        return { isValid: false, reason: "End date cannot be before start date" };
    }
    // Validate the range does not exceed maxRangeDays (if specified)
    if (maxRangeDays !== undefined && maxRangeDays > 0) {
        const diffMs = endDateObj.getTime() - startDateObj.getTime();
        const diffDays = Math.round(diffMs / MS_PER_DAY);
        if (diffDays >= maxRangeDays) {
            return {
                isValid: false,
                reason: `Date range cannot exceed ${maxRangeDays} days`
            };
        }
    }
    return { isValid: true };
};
const CustomDatePickerPopover = ({ initialEndDate = "", initialStartDate = "", maxRangeDays, onApply, onCancel, show, timezoneOffset = "UTC+00:00" }) => {
    // Normalize maxRangeDays: treat 0, negative, or absent values as "no limit".
    // This single source of truth is used by the picker cap, validation, helper text,
    // and the start-date-change clear logic — ensuring consistent behaviour everywhere.
    const effectiveMaxRangeDays = typeof maxRangeDays === "number" && maxRangeDays > 0 ? maxRangeDays : undefined;
    const [startDate, setStartDate] = React.useState(formatDateToMMDDYYYY(initialStartDate) || undefined);
    const [endDate, setEndDate] = React.useState(formatDateToMMDDYYYY(initialEndDate) || undefined);
    // Memoize today's date calculation to avoid recalculating on every render
    // Only recalculates when timezoneOffset changes
    const today = React.useMemo(() => {
        const now = new Date();
        // Extract offset hours from format like "UTC+01:00" or "+09:00"
        const offsetStr = timezoneOffset.replace("UTC", "");
        const offsetMatch = offsetStr.match(/([+-])(\d{1,2}):?(\d{2})?/);
        if (offsetMatch) {
            const sign = offsetMatch[1] === "+" ? 1 : -1;
            const hours = parseInt(offsetMatch[2], 10);
            const minutes = parseInt(offsetMatch[3] || "0", 10);
            const totalOffsetMinutes = sign * (hours * MINUTES_PER_HOUR + minutes);
            // Get UTC time and apply project timezone offset
            const utcTime = now.getTime() + now.getTimezoneOffset() * MS_PER_MINUTE;
            const projectTime = new Date(utcTime + totalOffsetMinutes * MS_PER_MINUTE);
            // Reset time to midnight to avoid timezone conversion issues
            projectTime.setHours(0, 0, 0, 0);
            return projectTime;
        }
        // Reset time to midnight
        now.setHours(0, 0, 0, 0);
        return now;
    }, [timezoneOffset]);
    // Format today's date for CatDatepickerField's disabledMaxDate prop (yyyy/MM/dd format)
    // This uses the project's timezone (from timezoneOffset prop) to determine "today"
    // The date picker will allow selection up to and including today in the project's timezone
    const todayForCatDatepicker = React.useMemo(() => {
        const month = String(today.getMonth() + 1).padStart(DATE_PAD_LENGTH, DATE_PAD_CHAR);
        const day = String(today.getDate()).padStart(DATE_PAD_LENGTH, DATE_PAD_CHAR);
        const year = today.getFullYear();
        return `${year}/${month}/${day}`;
    }, [today]);
    // When effectiveMaxRangeDays is set and a start date is selected, cap the end date picker's
    // disabledMaxDate to the earlier of (startDate + effectiveMaxRangeDays - 1) and today.
    const endDateMaxForCatDatepicker = React.useMemo(() => {
        if (!effectiveMaxRangeDays || !startDate)
            return todayForCatDatepicker;
        const startObj = parseDateString(startDate);
        if (!startObj)
            return todayForCatDatepicker;
        // startDate + (effectiveMaxRangeDays - 1) days = last valid end day (inclusive range)
        const rangeEnd = new Date(startObj);
        rangeEnd.setDate(startObj.getDate() + effectiveMaxRangeDays - 1);
        // Take the earlier of rangeEnd and today
        const effectiveMax = rangeEnd < today ? rangeEnd : today;
        const month = String(effectiveMax.getMonth() + 1).padStart(DATE_PAD_LENGTH, DATE_PAD_CHAR);
        const day = String(effectiveMax.getDate()).padStart(DATE_PAD_LENGTH, DATE_PAD_CHAR);
        const year = effectiveMax.getFullYear();
        return `${year}/${month}/${day}`;
    }, [effectiveMaxRangeDays, startDate, todayForCatDatepicker, today]);
    // Only update local state when popover becomes visible (show changes to true)
    // This prevents infinite loops when parent updates the dates after apply
    const prevShowRef = React.useRef(show);
    React.useEffect(() => {
        if (show && !prevShowRef.current) {
            // Popover just opened, sync with initial values
            const formattedStartDate = formatDateToMMDDYYYY(initialStartDate);
            const formattedEndDate = formatDateToMMDDYYYY(initialEndDate);
            setStartDate(formattedStartDate || undefined);
            setEndDate(formattedEndDate || undefined);
        }
        prevShowRef.current = show;
    }, [show, initialStartDate, initialEndDate]);
    const handleApply = () => {
        const validation = validateDateRange(startDate, endDate, today, effectiveMaxRangeDays);
        if (!validation.isValid) {
            if (validation.reason) {
                console.warn(validation.reason);
            }
            return;
        }
        // At this point, we know both dates are valid and startDate/endDate are not undefined
        onApply(startDate, endDate);
    };
    // Check if Apply button should be disabled
    const isApplyDisabled = () => {
        const validation = validateDateRange(startDate, endDate, today, effectiveMaxRangeDays);
        return !validation.isValid;
    };
    const handleCancel = () => {
        // Reset to initial values on cancel (convert to MM/dd/yyyy format)
        setStartDate(formatDateToMMDDYYYY(initialStartDate) || undefined);
        setEndDate(formatDateToMMDDYYYY(initialEndDate) || undefined);
        onCancel();
    };
    const handleStartDateChange = (e) => {
        const formattedDate = formatDateToMMDDYYYY(e.detail?.value);
        setStartDate(formattedDate);
        // Validate and adjust end date if needed using shared utility
        if (formattedDate && endDate) {
            const comparison = compareDates(endDate, formattedDate);
            // If comparison failed or end date is before start date, clear it
            if (comparison === null || comparison < 0) {
                setEndDate(undefined);
                return;
            }
            // If effectiveMaxRangeDays is set, also clear end date when it exceeds the limit
            if (effectiveMaxRangeDays) {
                const startObj = parseDateString(formattedDate);
                const endObj = parseDateString(endDate);
                if (startObj && endObj) {
                    const diffMs = endObj.getTime() - startObj.getTime();
                    const diffDays = Math.round(diffMs / MS_PER_DAY);
                    if (diffDays >= effectiveMaxRangeDays) {
                        setEndDate(undefined);
                    }
                }
            }
        }
    };
    const handleEndDateChange = (e) => {
        const formattedDate = formatDateToMMDDYYYY(e.detail?.value);
        setEndDate(formattedDate);
    };
    if (!show) {
        return null;
    }
    return (_jsx("div", { className: "mb-4 flex justify-end relative", children: _jsxs("div", { className: "absolute top-0 right-0 z-50 border rounded-lg bg-white shadow-lg w-[450px]", "data-test": "custom-date-picker-popover", children: [_jsx("div", { className: "p-4", children: _jsxs("div", { className: "flex gap-4", children: [_jsxs("div", { className: "flex-1", "data-test": "start-date-field-wrapper", children: [_jsxs("label", { className: "block text-sm font-medium text-gray-700 mb-2", "data-test": "start-date-label", children: ["Start Date ", _jsx("span", { className: "text-grey-500", children: "(required)" })] }), _jsx(CatDatepickerField, { ariaDescribedBy: "custom-start-date-picker", className: "w-full", "data-test": "start-date-picker", dateFormat: "MM/dd/yyyy", disabledMaxDate: todayForCatDatepicker, fieldId: "custom-start-date-picker", onBlDatePickerChange: handleStartDateChange, placeholder: "mm/dd/yyyy", size: "sm", value: startDate || "" }, `start-date-${todayForCatDatepicker}`), _jsx("div", { className: "!text-[10px] text-gray-600 mt-1", "data-test": "start-date-helper-text", children: "Cannot select dates after today" })] }), _jsxs("div", { className: "flex-1", "data-test": "end-date-field-wrapper", children: [_jsxs("label", { className: "block text-sm font-medium text-gray-700 mb-2", "data-test": "end-date-label", children: ["End Date ", _jsx("span", { className: "text-grey-500", children: "(required)" })] }), _jsx(CatDatepickerField, { ariaDescribedBy: "custom-end-date-picker", className: "w-full", "data-test": "end-date-picker", dateFormat: "MM/dd/yyyy", disabledMaxDate: endDateMaxForCatDatepicker, disabledMinDate: convertToYYYYMMDD(startDate), fieldId: "custom-end-date-picker", onBlDatePickerChange: handleEndDateChange, placeholder: "mm/dd/yyyy", size: "sm", value: endDate || "" }, `end-date-${endDateMaxForCatDatepicker}-${convertToYYYYMMDD(startDate)}`), _jsx("div", { className: "!text-[10px] text-gray-600 mt-1", "data-test": "end-date-helper-text", children: !startDate
                                            ? "Select start date first"
                                            : effectiveMaxRangeDays
                                                ? `Select a date range of ${effectiveMaxRangeDays} days or less`
                                                : "Must be on or after start date, up to today" })] })] }) }), _jsxs("div", { className: "border-t px-4 py-3 flex justify-start gap-2", "data-test": "popover-footer", children: [_jsx(CatButton, { className: "bg-yellow-400 text-black font-medium", "data-test": "apply-button", disabled: isApplyDisabled(), onClick: handleApply, variant: "primary", children: "Apply" }), _jsx(CatButton, { "data-test": "cancel-button", onClick: handleCancel, variant: "ghost", children: "Cancel" })] })] }) }));
};
export default CustomDatePickerPopover;
